/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/* generated using openapi-typescript-codegen -- do no edit */
/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AxiosError, AxiosResponse, AxiosInstance } from "axios";
import FormData from "form-data";

import type { ApiRequestOptions } from "./ApiRequestOptions";
import type { OnCancel } from "./CancelablePromise";
import type { OpenAPIConfig } from "./OpenAPI";
import { oidcService } from "@services/oidc/oidc";
import { FF_MOCK_AUTH } from "@shared/utils/feature-flag";
import { performSignout } from "@context/authentication-signout";
import { resolveAntiBotHeadersWithReload } from "./OpenAPI";
import { isRecaptchaDisabled } from "@shared/utils/recaptcha";
import { resolve } from "./request-headers";
import { sendRequest } from "./request-errors";
import {
  startChallenge,
  consumeChallengeToken,
  consumeChallengeTicket,
} from "@shared/utils/antibot-challenge-stash";
import { withSolveDeadline } from "@shared/utils/antibot-challenge-deadline";
import { emitTelemetry } from "@services/telemetry/telemetry";

/**
 * The collector's own URL. Retry/refresh telemetry for a telemetry POST is
 * suppressed so a flush can never recursively enqueue more telemetry.
 */
const TELEMETRY_URL_FRAGMENT = "/telemetry/events";

/**
 * Emit a retry-pipeline telemetry event, unless the request being retried IS
 * the telemetry collector itself (which would recurse).
 */
function emitRetryTelemetry(url: string, event: Parameters<typeof emitTelemetry>[0]): void {
  if (url.includes(TELEMETRY_URL_FRAGMENT)) return;
  emitTelemetry(event);
}

/**
 * Rebuilds the Authorization header by forcing an OIDC refresh-token grant,
 * forcing a sign-out only when the session genuinely cannot be recovered.
 *
 * This runs in response to a confirmed server-side 401: the backend has
 * already rejected the current access token as expired, so the local
 * near-expiry heuristic in `getValidAuthToken` is NOT trusted here — we force
 * a refresh-token grant (`forceRefreshToken`) regardless of what the client
 * thinks the token's `expires_at` is. The user is only signed out when there
 * is no refresh token to recover with, or when the refresh-token grant itself
 * fails to produce a usable new token. A merely-transient refresh failure with
 * a valid refresh token therefore no longer dumps the user on the error
 * boundary or logs them out — the retry recovers silently.
 *
 * Under mock auth it short-circuits to a static bearer token.
 *
 * @param headers - The current request headers (Authorization is replaced)
 * @returns The headers with an updated `Authorization` bearer token
 * @throws {Error} If the session cannot be recovered (no/failed refresh token)
 */
async function refreshAuthorizationHeader(headers: Record<string, string>): Promise<Record<string, string>> {
  if (FF_MOCK_AUTH) {
    return { ...headers, Authorization: `Bearer mock-access-token` };
  }
  if (!oidcService) return headers;

  const { forceRefreshToken } = await import('@services/auth/auth');
  const freshToken = await forceRefreshToken();

  if (!freshToken) {
    await performSignout();
    throw new Error('Token refresh failed - session expired');
  }

  return { ...headers, Authorization: `Bearer ${freshToken}` };
}

/**
 * Merges a freshly resolved `X-Anti-Bot` token into the existing headers, leaving
 * them unchanged when no fresh token is available.
 *
 * @param headers - The base request headers
 * @param freshHeaders - Newly resolved headers that may carry an anti-bot token
 * @returns The headers with the anti-bot token applied when present
 */
function mergeAntiBotHeader(
  headers: Record<string, string>,
  freshHeaders: Record<string, string> | undefined
): Record<string, string> {
  if (!freshHeaders?.['X-Anti-Bot']) return headers;
  return { ...headers, 'X-Anti-Bot': freshHeaders['X-Anti-Bot'] };
}

/**
 * Refreshes the auth token and anti-bot header, then replays the original request
 * once with the updated credentials.
 *
 * @param config - OpenAPI configuration used to send the request
 * @param options - The original request options
 * @param url - Fully resolved request URL
 * @param body - Request body payload
 * @param formData - Optional FormData for multipart requests
 * @param headers - The original request headers to refresh
 * @param onCancel - Cancellation callback
 * @param axiosClient - Axios instance used to replay the request
 * @returns The response from the retried request
 * @throws Rethrows any error raised while renewing the token or replaying
 */
export async function handleTokenRefreshAndRetry<T>(
  config: OpenAPIConfig,
  options: ApiRequestOptions,
  url: string,
  body: any,
  formData: FormData | undefined,
  headers: Record<string, string>,
  onCancel: OnCancel,
  axiosClient: AxiosInstance
): Promise<AxiosResponse<T>> {
  let tokenRefreshed = false;
  try {
    const oldToken = headers['Authorization'];
    let updatedHeaders = await refreshAuthorizationHeader(headers);
    tokenRefreshed = updatedHeaders['Authorization'] !== oldToken;
    const freshAntiBotHeaders = await resolve(options, config.HEADERS).catch(() => undefined);
    updatedHeaders = mergeAntiBotHeader(updatedHeaders, freshAntiBotHeaders);

    const response = await sendRequest<T>(config, options, url, body, formData, updatedHeaders, onCancel, axiosClient);
    // Auth event with the with-vs-without-refresh distinction and final outcome.
    emitRetryTelemetry(url, {
      event_type: 'auth',
      path: url,
      method: options.method,
      token_refreshed: tokenRefreshed,
      retried: true,
      auth_outcome: 'success',
    });
    return response;
  } catch (tokenRenewalError) {
    emitRetryTelemetry(url, {
      event_type: 'auth',
      path: url,
      method: options.method,
      token_refreshed: tokenRefreshed,
      retried: true,
      auth_outcome: 'failure',
    });
    console.warn("[request] Token renewal failed during request retry", tokenRenewalError);
    throw tokenRenewalError;
  }
}

/**
 * Error raised when the anti-bot retry cannot obtain a token, so replaying the
 * request would only reproduce the same rejection.
 */
export class AntiBotTokenUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AntiBotTokenUnavailableError';
  }
}

/**
 * handleAntiBotRetry function.
 *
 * @returns {*} The retried Axios response.
 */
export async function handleAntiBotRetry<T>(
  config: OpenAPIConfig,
  options: ApiRequestOptions,
  url: string,
  body: any,
  formData: FormData | undefined,
  headers: Record<string, string>,
  onCancel: OnCancel,
  axiosClient: AxiosInstance
): Promise<AxiosResponse<T>> {
  const freshAntiBotHeaders = await resolveAntiBotHeadersWithReload(options);
  const retryHeaders = { ...headers };

  if (freshAntiBotHeaders && freshAntiBotHeaders['X-Anti-Bot']) {
    retryHeaders['X-Anti-Bot'] = freshAntiBotHeaders['X-Anti-Bot'];
  }

  // We only get here because the server rejected the request for anti-bot
  // reasons. Replaying it without a token reproduces that rejection verbatim
  // (the backend answers a missing X-Anti-Bot header with 400), so the write
  // silently fails and the user is told nothing useful. Fail loudly instead.
  if (!retryHeaders['X-Anti-Bot'] && !isRecaptchaDisabled()) {
    console.error('[ANTIBOT] No token available for retry — aborting instead of replaying', {
      url,
      method: options.method,
    });
    emitRetryTelemetry(url, {
      event_type: 'antibot',
      path: url,
      method: options.method,
      retried: false,
      antibot_decision: 'BLOCK',
      antibot_reasons: 'no_token_for_retry',
    });
    throw new AntiBotTokenUnavailableError(
      'Could not obtain an anti-bot token. Reload the page and try again.',
    );
  }

  console.warn('[ANTIBOT] Retrying request with fresh token (after SDK reload)', {
    url,
    method: options.method,
    old_token_prefix: headers['X-Anti-Bot']?.slice(0, 20),
    new_token_prefix: retryHeaders['X-Anti-Bot']?.slice(0, 20),
  });

  try {
    const response = await sendRequest<T>(
      config,
      options,
      url,
      body,
      formData,
      retryHeaders,
      onCancel,
      axiosClient,
      true
    );
    emitRetryTelemetry(url, {
      event_type: 'antibot',
      path: url,
      method: options.method,
      retried: true,
      antibot_decision: 'ALLOW',
      auth_outcome: 'success',
    });
    return response;
  } catch (antiBotRetryError) {
    emitRetryTelemetry(url, {
      event_type: 'antibot',
      path: url,
      method: options.method,
      retried: true,
      antibot_decision: 'BLOCK',
      auth_outcome: 'failure',
    });
    throw antiBotRetryError;
  }
}

/** Header carrying the solved challenge token on a redeemed replay (D1). */
const CHALLENGE_HEADER = 'X-Anti-Bot-Challenge';
/** Header carrying the Fase 6 HMAC ticket, when the backend issued one. */
const CHALLENGE_TICKET_HEADER = 'X-Anti-Bot-Challenge-Ticket';

/**
 * Reads a string field off the 428's RFC-7807 body, or '' when absent/not a string.
 */
function readChallengeBodyField(axiosError: AxiosError, field: string): string {
  const responseBody = axiosError.response?.data as Record<string, unknown> | undefined;
  const value = responseBody?.[field];
  return typeof value === 'string' ? value : '';
}

/**
 * Reads the CHECKBOX site key the backend minted for this 428, from the
 * RFC-7807 body's `challenge_site_key` field (D11 -- the body is the primary
 * source; the env-configured key is only a fallback the overlay applies on
 * its own if this comes back empty).
 */
function extractChallengeSiteKey(axiosError: AxiosError): string {
  return readChallengeBodyField(axiosError, 'challenge_site_key');
}

/**
 * Reads the optional Fase 6 HMAC ticket from the 428 body's `challenge_ticket`
 * field. '' (not sent to the stash) when the backend didn't issue one --
 * non-blocking hardening, so its absence must never change behavior.
 */
function extractChallengeTicket(axiosError: AxiosError): string | null {
  const ticket = readChallengeBodyField(axiosError, 'challenge_ticket');
  return ticket || null;
}

/**
 * Opens the challenge overlay and joins its pending deferred (or starts a
 * new one) for the given site key.
 */
async function openChallengeOverlay(siteKey: string): Promise<void> {
  try {
    const { reportAntiBotError } = await import('@context/AntiBotContext');
    reportAntiBotError(new Error('Challenge required'), 428, siteKey);
  } catch {
    // AntiBotContext may not be available
  }
}

/**
 * Handles a CHALLENGE (428) response: opens the step-up overlay, awaits the
 * user solving it, then replays the original request exactly once with a
 * fresh `X-Anti-Bot` score token AND the solved `X-Anti-Bot-Challenge` token
 * attached (D1 -- the score check still runs normally on the replay; the
 * challenge only ever promotes CHALLENGE to ALLOW, never replaces the score
 * gate).
 *
 * Propagates {@link AntiBotChallengeCancelledError} unmodified when the user
 * cancels -- callers that care (e.g. usePaymentMethodSession) can distinguish
 * a cancellation from a real failure.
 *
 * @returns The response from the redeemed replay.
 */
export async function handleAntiBotChallenge<T>(
  axiosError: AxiosError,
  config: OpenAPIConfig,
  options: ApiRequestOptions,
  url: string,
  body: any,
  formData: FormData | undefined,
  headers: Record<string, string>,
  onCancel: OnCancel,
  axiosClient: AxiosInstance,
): Promise<AxiosResponse<T>> {
  const siteKey = extractChallengeSiteKey(axiosError);
  const ticket = extractChallengeTicket(axiosError);
  const challengeSettled = startChallenge(siteKey, ticket);
  await openChallengeOverlay(siteKey);

  // Rejects with AntiBotChallengeCancelledError if the user cancels -- left
  // unhandled so it propagates to the caller as-is.
  //
  // Bounded, because nothing else here guarantees the deferred ever settles.
  // AntiBotErrorOverlay is the only thing that resolves it and it is mounted
  // inside MainProvider alone, so the same 428 on PublicShell,
  // VistoryPublicShell or CallbackShell leaves this await pending forever:
  // the button spins, no error surfaces, no toast appears, and the user is
  // left with a page that is simply stuck. Generous on purpose -- somebody
  // working through an image challenge must never be cut off mid-solve, so
  // this is a stuck-forever guard, not a patience limit.
  await withSolveDeadline(challengeSettled);

  const challengeToken = consumeChallengeToken();
  if (!challengeToken) {
    // Solved but the token is gone (raced with another consumer, or aged
    // past the 100s stash TTL) -- surface the original 428 rather than
    // replaying without a challenge header, which would just repeat it.
    throw axiosError;
  }
  const challengeTicket = consumeChallengeTicket();

  const freshScoreHeaders = await resolveAntiBotHeadersWithReload(options);
  const retryHeaders: Record<string, string> = { ...headers, [CHALLENGE_HEADER]: challengeToken };
  if (challengeTicket) {
    retryHeaders[CHALLENGE_TICKET_HEADER] = challengeTicket;
  }
  if (freshScoreHeaders['X-Anti-Bot']) {
    retryHeaders['X-Anti-Bot'] = freshScoreHeaders['X-Anti-Bot'];
  }

  try {
    const response = await sendRequest<T>(
      config,
      options,
      url,
      body,
      formData,
      retryHeaders,
      onCancel,
      axiosClient,
      true
    );
    emitRetryTelemetry(url, {
      event_type: 'antibot',
      path: url,
      method: options.method,
      retried: true,
      antibot_decision: 'CHALLENGE',
      auth_outcome: 'success',
    });
    return response;
  } catch (challengeReplayError) {
    emitRetryTelemetry(url, {
      event_type: 'antibot',
      path: url,
      method: options.method,
      retried: true,
      antibot_decision: 'CHALLENGE',
      auth_outcome: 'failure',
    });
    throw challengeReplayError;
  }
}
